import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select, SelectOption } from '../../components/ui/Select';
import { LoadingState } from '../../components/ui/LoadingState';
import { EmptyState } from '../../components/ui/EmptyState';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  id: number;
  email: string;
  full_name: string;
  role: {
    id: number;
    name: string;
    display_name: string;
  };
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  last_login?: string;
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Fetch users
  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const response = (await apiService.getUsers()) as {
        users?: User[];
        data?: User[];
      };
      return response.users || response.data || [];
    },
  });

  const roleOptions: SelectOption[] = [
    { value: 'admin', label: 'Administrator' },
    { value: 'manager', label: 'Manager' },
    { value: 'analyst', label: 'Analyst' },
    { value: 'viewer', label: 'Viewer' },
  ];

  const handleEditRole = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDeactivate = async (userId: number) => {
    // Implement deactivation
    console.log('Deactivate user:', userId);
  };

  if (isLoading) {
    return <LoadingState message="Loading users..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage users, roles, and permissions for your organization
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Invite User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card variant="elevated">
          <CardContent>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">{users?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card variant="elevated">
          <CardContent>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="mt-2 text-3xl font-bold text-success-600">
                {users?.filter((u) => u.is_active).length || 0}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card variant="elevated">
          <CardContent>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Verified</p>
              <p className="mt-2 text-3xl font-bold text-primary-600">
                {users?.filter((u) => u.is_verified).length || 0}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card variant="elevated">
          <CardContent>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Administrators</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {users?.filter((u) => u.role.name === 'admin').length || 0}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          {!users || users.length === 0 ? (
            <EmptyState
              title="No users found"
              description="Get started by inviting your first user"
              action={{
                label: 'Invite User',
                onClick: () => setIsCreateModalOpen(true),
              }}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-700 font-semibold">
                              {user.full_name.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="primary">{user.role.display_name}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {user.is_active && (
                            <Badge variant="success" dot>
                              Active
                            </Badge>
                          )}
                          {!user.is_active && (
                            <Badge variant="danger" dot>
                              Inactive
                            </Badge>
                          )}
                          {user.is_verified && (
                            <Badge variant="info" size="sm">
                              Verified
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditRole(user)}>
                            Edit Role
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeactivate(user.id)}
                            >
                              Deactivate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Invite New User"
        description="Send an invitation email to add a new user to your organization"
      >
        <div className="space-y-4">
          <Input label="Email Address" type="email" placeholder="user@example.com" required />
          <Input label="Full Name" placeholder="John Doe" required />
          <Select
            label="Role"
            value="analyst"
            onChange={() => {}}
            options={roleOptions}
            required
          />
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => setIsCreateModalOpen(false)}>Send Invitation</Button>
        </ModalFooter>
      </Modal>

      {/* Edit Role Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Change User Role"
        description={`Update the role for ${selectedUser?.full_name}`}
      >
        <div className="space-y-4">
          <Select
            label="New Role"
            value={selectedUser?.role.name || ''}
            onChange={() => {}}
            options={roleOptions}
            required
          />
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => setIsEditModalOpen(false)}>Update Role</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
